import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import {
  createCancellationPolicy,
  getAllActiveCancellationPolicy,
  getAllCancellationPolicy,
  getCancellationPolicyByID,
  updateCancellationPolicy,
} from "./services/cancellationpolicy.services";
import { clearCancellationPolicyDetails } from "./cancellationpolicySlice";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import IconButton from "../../components/common/IconButton";

const CreateCancellationPolicy: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const CancellationPolicyDetail = useAppSelector(
    (state) => state.cancellationpolicy.CancellationPolicyDetail
  );
  const CancellationPolicyID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.cancellationpolicy.formLoading
  );
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    if (CancellationPolicyID) {
      dispatch(getCancellationPolicyByID(CancellationPolicyID));
    }
  }, [CancellationPolicyID]);

  useEffect(() => {
    return () => {
      dispatch(clearCancellationPolicyDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!CancellationPolicyID) {
        const insertPayload = {
          Title: values.Title,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(
          createCancellationPolicy(insertPayload)
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCancellationPolicyDetails());

          if (isIndirect) {
            dispatch(getAllActiveCancellationPolicy());
          } else {
            dispatch(getAllCancellationPolicy());
          }
        }
      } else {
        const editPayload = {
          ID: CancellationPolicyID,
          Title: values.Title,
          Description: values.Description,

          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateCancellationPolicy(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCancellationPolicyDetails());
          dispatch(getAllCancellationPolicy());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CancellationPolicyDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="mui-drawer-header">
              <Typography.h4 className="m-0">
                {CancellationPolicyID
                  ? "Update Cancellation Policy"
                  : "Add Cancellation Policy"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Title"
                label="Title"
                placeholder="i.e Cancellation Policy"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Description"
                label="Description"
                component={FormRichTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="IsActive"
                label="Status"
                astrike={true}
                component={FormSelectionField}
                validator={requiredValidator}
                placeholder="i.e. Active"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status?.value,
                    label: status?.label,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={!CancellationPolicyID ? "Save" : "Update"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateCancellationPolicy;
